import React, { useEffect, useState } from 'react';
import { Typography, Box, Theme } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import { observer, inject } from 'mobx-react';

import StoreModel from 'models/Index';
import { Services, history, RoutePaths } from 'helpers';
import { NextButton, PrevButton } from 'components/wizard';
import Header from 'components/admin/Header';
import { CassetteWarningModal } from 'containers/modals';

interface CollectionSummaryProps extends WithStyles<typeof styles> {}
interface CollectionSummaryInject extends CollectionSummaryProps {
    Store: StoreModel;
}

const styles = (theme: Theme) =>
    createStyles({
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            height: '170px',
        },
        headerLine: {
            height: '6px',
            background: '#e0e0e0',
        },
        main: {
            margin: '0px 60px',
        },
        title: {
            marginTop: '66px',
        },
        subTitle: {
            marginTop: '36px',
        },
        tableHeader: {
            marginTop: '70px',
            display: 'flex',
            fontWeight: 'bold',
        },
        tableBox: {
            padding: '40px 0px 13px 0px',
            display: 'flex',
            flexDirection: 'column',
            gap: '17px',
        },
        tableRow: {
            display: 'flex',
        },
        denominationAmountColumn: {
            fontWeight: 'inherit',
            flex: '0.75',
        },
        tableValue: {
            width: '80px',
            textAlign: 'end',
        },
        denominationCountColumn: {
            fontWeight: 'inherit',
            flex: '0.25',
        },
        denominationValueColumn: {
            fontWeight: 'inherit',
            flex: '0',
        },
        summaryBox: {
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
        },
        summaryBoxItem: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '75px',
        },
        totalLabel: {
            width: '150px',
        },
        totalResult: {
            width: '110px',
            textAlign: 'right',
        },
        buttonBox: {
            display: 'flex',
            left: 60,
            right: 60,
            position: 'fixed',
            bottom: '105px',
            justifyContent: 'space-between',
        },
        underline: {
            marginTop: '10px',
            borderTop: '1px solid #343434',
        },
    });

const CollectionSummary = ({ classes, Store }: CollectionSummaryInject) => {
    const { userCashMetrics, submitReport } = Store.collection;
    const currencySign = userCashMetrics?.currencySign;
    const [isOpenCassetteWarningModal, setIsOpenCassetteWarningModal] =
        useState(false);

    useEffect(() => {
        if (!userCashMetrics) {
            history.push({
                pathname: RoutePaths.CashCollection,
                search: window.location.search,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Box className={classes.header}>
                <Header />
            </Box>
            <div className={classes.headerLine}></div>

            <Box className={classes.main}>
                <Typography
                    color='primary'
                    variant='h2'
                    align='center'
                    className={classes.title}
                >
                    Cash Collection
                </Typography>

                <Typography
                    variant='h4'
                    align='center'
                    className={classes.subTitle}
                >
                    Review the collection summary and submit.
                </Typography>

                <Box className={classes.tableHeader}>
                    <Typography
                        className={classes.denominationAmountColumn}
                        variant='h5'
                        align='left'
                    >
                        Denomination
                    </Typography>
                    <Typography
                        className={classes.denominationCountColumn}
                        variant='h5'
                        align='left'
                    >
                        Count
                    </Typography>
                    <Typography
                        className={classes.denominationValueColumn}
                        variant='h5'
                        align='left'
                    >
                        Value
                    </Typography>
                </Box>

                <hr className={classes.underline} />

                <Box className={classes.tableBox}>
                    {userCashMetrics?.denominations.map(
                        (denomination, index) => {
                            return (
                                <Box key={index} className={classes.tableRow}>
                                    <Box
                                        className={
                                            classes.denominationAmountColumn
                                        }
                                    >
                                        <Typography variant='h5'>{`${currencySign}${denomination.denomination}`}</Typography>
                                    </Box>

                                    <Box
                                        className={
                                            classes.denominationCountColumn
                                        }
                                    >
                                        <Typography
                                            variant='h5'
                                            className={classes.tableValue}
                                        >
                                            {denomination.count}
                                        </Typography>
                                    </Box>

                                    <Box
                                        className={
                                            classes.denominationValueColumn
                                        }
                                    >
                                        <Typography
                                            variant='h5'
                                            className={classes.tableValue}
                                        >
                                            {`${currencySign}${denomination.amount.toLocaleString(
                                                'en-US'
                                            )}`}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }
                    )}
                </Box>

                <hr className={classes.underline} />

                <Box className={classes.summaryBox}>
                    <Box className={classes.summaryBoxItem}>
                        <Typography
                            className={classes.totalLabel}
                            variant='h4'
                            align='left'
                        >
                            Total Count
                        </Typography>

                        <Typography
                            className={classes.totalResult}
                            variant='h4'
                            align='left'
                        >
                            {`${userCashMetrics?.totalCount}`}
                        </Typography>
                    </Box>
                    <Box className={classes.summaryBoxItem}>
                        <Typography
                            className={classes.totalLabel}
                            variant='h4'
                            align='left'
                        >
                            Total Value
                        </Typography>

                        <Typography
                            className={classes.totalResult}
                            variant='h4'
                            align='left'
                        >
                            {`${currencySign}${userCashMetrics?.totalAmount.toLocaleString(
                                'en-US'
                            )}`}
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.buttonBox}>
                    <PrevButton
                        buttonProps={{
                            onClick: () =>
                                history.push({
                                    pathname: RoutePaths.CashCollection,
                                    search: window.location.search,
                                }),
                        }}
                    />
                    <NextButton
                        title='Submit'
                        buttonProps={{
                            onClick: async () => {
                                try {
                                    if (
                                        Store.peripheryStatus.isCassetteInserted
                                    ) {
                                        await submitReport();

                                        history.push({
                                            pathname:
                                                RoutePaths.CashCollectionSuccess,
                                            search: window.location.search,
                                        });
                                    } else {
                                        setIsOpenCassetteWarningModal(true);
                                    }
                                } catch (e: any) {
                                    console.log(
                                        'Unable to submit cash collection: ',
                                        e
                                    );
                                    Store.snackbar.showMessage(
                                        'error',
                                        'Unable to submit cash collection, please try again.'
                                    );
                                }
                            },
                        }}
                    />
                </Box>
            </Box>
            <CassetteWarningModal
                isOpen={isOpenCassetteWarningModal}
                onClose={() => setIsOpenCassetteWarningModal(false)}
            />
        </>
    );
};

export default withStyles(styles)(
    inject(Services.STORE)(observer(CollectionSummary))
);
